import { Buffer } from 'buffer';
globalThis.Buffer = Buffer;

import './styles/main.css';
import { DataLoader } from './services/dataLoader';
import { Radar } from './components/Radar';
import { EntryDetail } from './components/EntryDetail';
import { FilterPanel } from './components/FilterPanel';
import type { RadarData, RadarEntry } from './types/radar';

/**
 * Main application class
 */
class TechRadarApp {
  private loader: DataLoader;
  private radar: Radar | null = null;
  private entryDetail: EntryDetail;
  private filterPanel: FilterPanel | null = null;
  private data: RadarData | null = null;
  private filteredData: RadarData | null = null;

  constructor() {
    this.loader = new DataLoader();
    this.entryDetail = new EntryDetail();
  }

  /**
   * Initialize and render the application
   */
  async init(): Promise<void> {
    try {
      this.showLoading();

      // Load data
      this.data = await this.loader.load();
      this.filteredData = this.data;

      // Render UI
      this.renderHeader();
      this.renderFilterPanel();
      this.renderRadar();
      this.renderEntryList();
      this.setupCollapsibleSections();

      this.hideLoading();
    } catch (error) {
      console.error('Failed to initialize Tech Radar:', error);
      this.showError('Failed to load Tech Radar data. Please try again later.');
    }
  }

  /**
   * Render header
   */
  private renderHeader(): void {
    if (!this.data) return;

    const header = document.getElementById('header');
    if (!header) return;

    header.innerHTML = `
      <div class="header-content">
        <h1>${this.data.config.title}</h1>
        <p class="header-description">${this.data.config.description}</p>
        <p class="header-meta">Last Updated: ${this.data.lastUpdated.toLocaleDateString()}</p>
      </div>
    `;
  }

  /**
   * Render filter panel
   */
  private renderFilterPanel(): void {
    if (!this.data) return;

    const filterContainer = document.getElementById('filters');
    if (!filterContainer) return;

    // Collect all unique tags
    const allTags = new Set<string>();
    this.data.entries.forEach((entry) => {
      entry.tags.forEach((tag) => allTags.add(tag));
    });

    this.filterPanel = new FilterPanel(filterContainer, this.data.config, Array.from(allTags));
    this.filterPanel.setChangeHandler((filterState) => {
      this.applyFilters(filterState);
    });
  }

  /**
   * Render radar visualization
   */
  private renderRadar(): void {
    if (!this.filteredData) return;

    const radarContainer = document.getElementById('radar');
    if (!radarContainer) return;

    // Clear existing radar
    radarContainer.innerHTML = '';

    // Create new radar
    this.radar = new Radar(radarContainer, this.filteredData, {
      width: Math.min(window.innerWidth - 40, 1200),
      height: 1000,
    });

    this.radar.setBlipClickHandler((entry) => {
      this.entryDetail.show(entry);
    });

    this.radar.setQuadrantClickHandler((quadrantId) => {
      // Set the quadrant filter in the filter panel
      if (this.filterPanel) {
        const currentState = this.filterPanel.getState();
        // Toggle quadrant - if already selected, clear it; otherwise select it
        const newQuadrant = currentState.quadrant === quadrantId ? undefined : quadrantId;
        this.filterPanel.setState({ quadrant: newQuadrant });
        this.applyFilters({ ...currentState, quadrant: newQuadrant });
      }
    });

    this.radar.render();
  }

  /**
   * Render entry list
   */
  private renderEntryList(): void {
    if (!this.filteredData) return;

    const listContainer = document.getElementById('entry-list');
    if (!listContainer) return;

    // Group entries by ring
    const byRing = new Map<string, RadarEntry[]>();
    this.filteredData.config.rings.forEach((ring) => {
      byRing.set(ring.id, []);
    });

    this.filteredData.entries.forEach((entry) => {
      byRing.get(entry.ring)?.push(entry);
    });

    // Sort entries alphabetically within each ring
    byRing.forEach((entries) => {
      entries.sort((a, b) => a.title.localeCompare(b.title));
    });

    const totalCount = this.filteredData.entries.length;

    // Update the toggle button text with count
    const toggleBtn = document.getElementById('entry-list-toggle');
    if (toggleBtn) {
      const toggleText = toggleBtn.querySelector('span:last-child');
      if (toggleText) {
        toggleText.textContent = `Technologies (${totalCount})`;
      }
    }

    let html = `<div class="entry-list-content">`;

    this.filteredData.config.rings.forEach((ring) => {
      const entries = byRing.get(ring.id) || [];
      if (entries.length === 0) return;

      html += `
        <div class="entry-ring-section entry-ring-${ring.id}">
          <h4 class="entry-ring-title">
            ${ring.name} <span class="entry-ring-count">${entries.length}</span>
          </h4>
          <ul class="entry-list-items">
            ${entries
              .map((entry) => {
                let badge = '';
                if (entry.change.status === 'new') {
                  badge = '<span class="badge badge-new">New</span>';
                } else if (entry.change.status === 'moved') {
                  const arrow = entry.change.direction === 'inward' ? '↑' : '↓';
                  badge = `<span class="badge badge-moved">${arrow}</span>`;
                }

                return `
                  <li class="entry-list-item" data-id="${entry.id}">
                    <span class="entry-list-title">${entry.title}</span>
                    ${badge}
                    <span class="entry-list-quadrant">${entry.quadrant.replace('-', ' & ')}</span>
                  </li>
                `;
              })
              .join('')}
          </ul>
        </div>
      `;
    });

    html += '</div>';
    listContainer.innerHTML = html;

    // Attach click handlers
    listContainer.querySelectorAll('.entry-list-item').forEach((item) => {
      item.addEventListener('click', () => {
        const id = item.getAttribute('data-id');
        const entry = this.filteredData?.entries.find((e) => e.id === id);
        if (entry) {
          this.entryDetail.show(entry);
        }
      });
    });
  }

  /**
   * Apply filters
   */
  private applyFilters(filterState: any): void {
    if (!this.data) return;

    const filtered = this.loader.filterEntries(this.data.entries, {
      quadrant: filterState.quadrant,
      ring: filterState.ring,
      tags: filterState.tags,
      search: filterState.search,
    });

    // Filter by change status
    const finalFiltered =
      filterState.changeStatus
        ? filtered.filter((e) => e.change.status === filterState.changeStatus)
        : filtered;

    this.filteredData = {
      ...this.data,
      entries: finalFiltered,
    };

    this.renderRadar();
    this.renderEntryList();
  }

  /**
   * Show loading state
   */
  private showLoading(): void {
    const app = document.getElementById('app');
    if (app) {
      const loadingDiv = document.createElement('div');
      loadingDiv.className = 'loading';
      loadingDiv.textContent = 'Loading Tech Radar...';
      app.prepend(loadingDiv);
    }
  }

  /**
   * Hide loading state
   */
  private hideLoading(): void {
    const loading = document.querySelector('.loading');
    loading?.remove();
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = `<div class="error">${message}</div>`;
    }
  }

  /**
   * Setup collapsible sections for filters and entry list
   */
  private setupCollapsibleSections(): void {
    const filterToggle = document.getElementById('filter-toggle');
    const filterContent = document.getElementById('filters');
    const entryListToggle = document.getElementById('entry-list-toggle');
    const entryListContent = document.getElementById('entry-list');

    if (filterToggle && filterContent) {
      filterToggle.addEventListener('click', () => {
        const isExpanded = filterToggle.getAttribute('aria-expanded') === 'true';
        filterToggle.setAttribute('aria-expanded', String(!isExpanded));
        filterContent.style.display = isExpanded ? 'none' : 'block';
        const icon = filterToggle.querySelector('.toggle-icon');
        if (icon) {
          icon.textContent = isExpanded ? '▶' : '▼';
        }
      });
    }

    if (entryListToggle && entryListContent) {
      entryListToggle.addEventListener('click', () => {
        const isExpanded = entryListToggle.getAttribute('aria-expanded') === 'true';
        entryListToggle.setAttribute('aria-expanded', String(!isExpanded));
        entryListContent.style.display = isExpanded ? 'none' : 'block';
        const icon = entryListToggle.querySelector('.toggle-icon');
        if (icon) {
          icon.textContent = isExpanded ? '▶' : '▼';
        }
      });
    }
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const app = new TechRadarApp();
  app.init();
});
